import { ChevronRight, LocateFixed, Plus, Search, Trash2 } from 'lucide-react';
import { useCallback, useState, type FormEvent } from 'react';
import { usePolling } from '../../hooks/usePolling';
import { endpoints } from '../../services/api';
import { startMicrosoft, useSession } from '../../store/session';
import { currentPosition, searchPlaces } from '../../services/weather';
import { deviceZone, zoneCity } from '../../lib/time';
import { newId, useSettings, type ClockPosition, type Location } from '../../store/settings';
import { buttonClass, Choice, ghostButtonClass, inputClass, Section, Switch, Toggle, WidgetPage } from './controls';
import { WIDGET_PAGES, type WidgetPageId } from './widgetPages';

const ZONES: string[] = (() => {
  try {
    return (Intl as unknown as { supportedValuesOf?: (k: string) => string[] }).supportedValuesOf?.('timeZone') ?? [];
  } catch {
    return [];
  }
})();

/** Settings → Widgets: Photos only, and every widget with a quick on/off and a link to its page. */
export function WidgetsOverview({ go }: { go: (tab: string) => void }) {
  const s = useSettings();
  const update = s.update;
  const VIEW = { list: 'List', day: 'Day', week: 'Week', month: 'Month' } as const;
  const summary: Record<WidgetPageId, string> = {
    clock: [
      s.clock.face === 'digital' ? `Digital, ${s.clock.hour12 ? '12' : '24'}-hour` : 'Analogue',
      s.clock.timeZone ? s.clock.timeZone.replace(/_/g, ' ') : '',
      s.clock.extraZones?.length ? `+${s.clock.extraZones.length} more clock${s.clock.extraZones.length === 1 ? '' : 's'}` : '',
    ]
      .filter(Boolean)
      .join(' · '),
    location: `Where & when each photo was taken${s.photoInfo.showMap ? ', with a map' : ''}`,
    weather: s.weather.location?.name ?? 'No location set',
    calendar: `${VIEW[s.calendar.view]} view`,
    lists: s.lists.source === 'microsoft' ? 'Microsoft To Do' : 'KCL Canvas lists',
    news: { ticker: 'Ticker', card: 'Story card', both: 'Ticker + story card' }[s.news.display],
  };

  return (
    <>
      <Section
        title="Photos only"
        description="Show just your photos and videos – no clock, location, weather, calendar, lists or news. Your widget settings are kept for when you switch back."
      >
        <Toggle label="Photos only" checked={s.photosOnly} onChange={(v) => update('photosOnly', v)} />
      </Section>
      <Section title="Widgets" description={s.photosOnly ? 'Hidden while Photos only is on.' : 'Switch each one on or off, or open it for its options.'}>
        <ul className="space-y-2">
          {WIDGET_PAGES.map((w) => (
            <li key={w.id} className="flex items-center gap-3 rounded-xl border border-[var(--border)] p-3">
              <w.icon className="h-5 w-5 shrink-0 opacity-80" aria-hidden />
              <button type="button" className="min-w-0 flex-1 text-left" onClick={() => go(w.id)}>
                <div className="text-sm font-medium">{w.name}</div>
                <div className="muted truncate text-xs">{s[w.key].enabled ? summary[w.id] : 'Off'}</div>
              </button>
              <Switch label={`Show ${w.name}`} checked={s[w.key].enabled} onChange={(enabled) => update(w.key, { enabled })} />
              <button
                type="button"
                onClick={() => go(w.id)}
                aria-label={`${w.name} options`}
                className="rounded-lg p-1.5 hover:bg-[var(--border)]"
              >
                <ChevronRight className="h-5 w-5" aria-hidden />
              </button>
            </li>
          ))}
        </ul>
      </Section>
    </>
  );
}

function ZoneSelect({ value, onChange, allowDevice, label }: { value: string; onChange: (z: string) => void; allowDevice?: boolean; label: string }) {
  return (
    <select className={`${inputClass} bg-[var(--surface-strong)]`} value={value} onChange={(e) => onChange(e.target.value)} aria-label={label}>
      {allowDevice && <option value="">This device ({deviceZone().replace(/_/g, ' ')})</option>}
      {!allowDevice && !ZONES.includes(value) && <option value={value}>{value.replace(/_/g, ' ')}</option>}
      {ZONES.map((z) => (
        <option key={z} value={z}>
          {z.replace(/_/g, ' ')}
        </option>
      ))}
    </select>
  );
}

const POSITIONS: Array<{ id: ClockPosition; name: string }> = [
  { id: 'top-left', name: 'Top left' },
  { id: 'top-center', name: 'Top centre' },
  { id: 'top-right', name: 'Top right' },
  { id: 'middle-left', name: 'Middle left' },
  { id: 'middle-center', name: 'Centre' },
  { id: 'middle-right', name: 'Middle right' },
  { id: 'bottom-left', name: 'Bottom left' },
  { id: 'bottom-center', name: 'Bottom centre' },
  { id: 'bottom-right', name: 'Bottom right' },
];

/** A 3×3 grid of spots: where the clock sits in its area. */
function PositionPicker({ value, onChange }: { value: ClockPosition; onChange: (p: ClockPosition) => void }) {
  return (
    <div className="grid w-48 grid-cols-3 gap-1.5 rounded-xl border border-[var(--border)] p-1.5" role="radiogroup" aria-label="Clock position">
      {POSITIONS.map((p) => (
        <button
          key={p.id}
          type="button"
          role="radio"
          aria-checked={value === p.id}
          aria-label={p.name}
          title={p.name}
          onClick={() => onChange(p.id)}
          className={`flex aspect-[16/10] items-center justify-center rounded-md border transition ${
            value === p.id ? 'border-[var(--accent)] bg-[var(--accent)]/25' : 'border-[var(--border)] hover:border-[var(--fg-muted)]'
          }`}
        >
          <span className={`h-2 w-2 rounded-full ${value === p.id ? 'bg-[var(--accent)]' : 'bg-[var(--fg-muted)]/50'}`} />
        </button>
      ))}
    </div>
  );
}

const MAX_EXTRA_CLOCKS = 6;

export function ClockTab() {
  const clock = useSettings((s) => s.clock);
  const update = useSettings((s) => s.update);
  const extras = clock.extraZones ?? [];
  const setExtras = (extraZones: typeof extras) => update('clock', { extraZones });
  return (
    <WidgetPage label="Show clock" enabled={clock.enabled} onChange={(enabled) => update('clock', { enabled })}>
      <Section title="Clock face">
        <Choice
          options={[
            { id: 'digital', name: 'Digital', description: '12:45' },
            { id: 'analogue', name: 'Analogue', description: 'With numbers' },
            { id: 'analogue-plain', name: 'Analogue', description: 'Marks only, no numbers' },
          ]}
          value={clock.face}
          onChange={(face) => update('clock', { face })}
          columns={3}
        />
        <Toggle label="12-hour clock" checked={clock.hour12} onChange={(hour12) => update('clock', { hour12 })} />
        <Toggle
          label={clock.face === 'digital' ? 'Show seconds' : 'Second hand'}
          checked={clock.showSeconds}
          onChange={(showSeconds) => update('clock', { showSeconds })}
        />
        <Toggle label="Show date" checked={clock.showDate} onChange={(showDate) => update('clock', { showDate })} />
        <Toggle
          label="Show in a box (like the other widgets – see Appearance → Widget style)"
          checked={clock.boxed}
          onChange={(boxed) => update('clock', { boxed })}
        />
      </Section>

      <Section
        title="Position"
        description="Where the clock sits in its area: its box in the Custom layout, or its part of the screen. Some layouts only have room to move it left, centre or right."
      >
        <PositionPicker value={clock.position} onChange={(position) => update('clock', { position })} />
        <Choice
          options={[
            { id: 'left', name: 'Left' },
            { id: 'center', name: 'Centre' },
            { id: 'right', name: 'Right' },
            { id: 'justify', name: 'Justify', description: 'Lines match the time' },
          ]}
          value={clock.align}
          onChange={(align) => update('clock', { align })}
          columns={4}
        />
      </Section>

      <Section title="Time zone">
        <ZoneSelect label="Time zone" value={clock.timeZone} onChange={(timeZone) => update('clock', { timeZone })} allowDevice />
        <input
          className={inputClass}
          placeholder={`Place name (optional, e.g. Home) – otherwise “${zoneCity(clock.timeZone || deviceZone())}”`}
          value={clock.label}
          maxLength={40}
          onChange={(e) => update('clock', { label: e.target.value })}
          aria-label="Place name"
        />
        <Toggle label="Show the place name" checked={clock.showPlace || extras.length > 0} onChange={(showPlace) => update('clock', { showPlace })} />
        <Toggle label="Show the UTC offset (e.g. UTC+1)" checked={clock.showOffset || extras.length > 0} onChange={(showOffset) => update('clock', { showOffset })} />
        {extras.length > 0 && <p className="muted text-xs">With more than one clock, each always shows its place and UTC offset.</p>}
      </Section>

      <Section title="More clocks" description="Other time zones, shown under the main clock with their place and UTC offset.">
        {extras.length > 0 && (
          <ul className="space-y-2">
            {extras.map((z) => (
              <li key={z.id} className="space-y-2 rounded-lg border border-[var(--border)] p-3">
                <div className="flex gap-2">
                  <ZoneSelect
                    label="Time zone"
                    value={z.timeZone}
                    onChange={(timeZone) => setExtras(extras.map((x) => (x.id === z.id ? { ...x, timeZone } : x)))}
                  />
                  <button
                    type="button"
                    className={ghostButtonClass}
                    onClick={() => setExtras(extras.filter((x) => x.id !== z.id))}
                    aria-label={`Remove ${z.label || zoneCity(z.timeZone)}`}
                  >
                    <Trash2 className="h-4 w-4" aria-hidden />
                  </button>
                </div>
                <input
                  className={inputClass}
                  placeholder={`Place name (optional) – otherwise “${zoneCity(z.timeZone)}”`}
                  value={z.label}
                  maxLength={40}
                  onChange={(e) => setExtras(extras.map((x) => (x.id === z.id ? { ...x, label: e.target.value } : x)))}
                  aria-label="Place name"
                />
              </li>
            ))}
          </ul>
        )}
        {extras.length < MAX_EXTRA_CLOCKS && (
          <button
            type="button"
            className={ghostButtonClass}
            onClick={() => setExtras([...extras, { id: newId(), timeZone: extras.length ? 'Asia/Tokyo' : 'America/New_York', label: '' }])}
          >
            <Plus className="h-4 w-4" aria-hidden /> Add a clock
          </button>
        )}
      </Section>
    </WidgetPage>
  );
}

export function WeatherTab() {
  const weather = useSettings((s) => s.weather);
  const update = useSettings((s) => s.update);

  const [query, setQuery] = useState('');
  const [results, setResults] = useState<Location[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const search = async (e: FormEvent) => {
    e.preventDefault();
    if (!query.trim()) return;
    setBusy(true);
    setError(null);
    try {
      const r = await searchPlaces(query.trim());
      setResults(r);
      if (!r.length) setError('No places found.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Search failed');
    } finally {
      setBusy(false);
    }
  };

  const locate = async () => {
    setBusy(true);
    setError(null);
    try {
      update('weather', { location: await currentPosition() });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not get location');
    } finally {
      setBusy(false);
    }
  };

  return (
    <WidgetPage
      label="Show weather"
      description="7-day forecast from Open-Meteo."
      enabled={weather.enabled}
      onChange={(enabled) => update('weather', { enabled })}
    >
      <Section title="Location">
        <p className="text-sm">
          Location: <strong>{weather.location?.name ?? 'not set'}</strong>
        </p>
        <form onSubmit={search} className="flex gap-2">
          <input
            className={inputClass}
            placeholder="Search a town or city"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            aria-label="Search location"
          />
          <button className={buttonClass} disabled={busy} type="submit" aria-label="Search">
            <Search className="h-4 w-4" aria-hidden />
          </button>
          <button className={ghostButtonClass} disabled={busy} type="button" onClick={locate} aria-label="Use my location">
            <LocateFixed className="h-4 w-4" aria-hidden />
          </button>
        </form>
        {error && <p className="text-sm text-red-400">{error}</p>}
        {results.length > 0 && (
          <ul className="space-y-1">
            {results.map((r) => (
              <li key={`${r.latitude},${r.longitude}`}>
                <button
                  className="w-full rounded-lg px-3 py-2 text-left text-sm hover:bg-[var(--border)]"
                  onClick={() => {
                    update('weather', { location: r });
                    setResults([]);
                    setQuery('');
                  }}
                >
                  {r.name}
                </button>
              </li>
            ))}
          </ul>
        )}
      </Section>
      <Section title="Show">
        <Toggle
          label="Fahrenheit"
          checked={weather.unit === 'fahrenheit'}
          onChange={(f) => update('weather', { unit: f ? 'fahrenheit' : 'celsius' })}
        />
        <Toggle label="Hourly forecast" checked={weather.showHourly} onChange={(showHourly) => update('weather', { showHourly })} />
        <Toggle label="7-day forecast" checked={weather.showDaily} onChange={(showDaily) => update('weather', { showDaily })} />
        <Toggle
          label="Weather warnings (official, via MeteoAlarm – Europe)"
          checked={weather.showWarnings}
          onChange={(showWarnings) => update('weather', { showWarnings })}
        />
        {weather.showWarnings && weather.location && !weather.location.countryCode && (
          <p className="muted text-xs">Warnings need a searched location – search for your town above.</p>
        )}
        <Toggle label="Feels-like temperature" checked={weather.showFeelsLike} onChange={(showFeelsLike) => update('weather', { showFeelsLike })} />
        <Toggle label="Chance of rain" checked={weather.showRain} onChange={(showRain) => update('weather', { showRain })} />
        <Toggle label="Wind" checked={weather.showWind} onChange={(showWind) => update('weather', { showWind })} />
        {weather.showWind && (
          <Choice
            options={[
              { id: 'kmh', name: 'km/h' },
              { id: 'mph', name: 'mph' },
              { id: 'ms', name: 'm/s' },
            ]}
            value={weather.windUnit}
            onChange={(windUnit) => update('weather', { windUnit })}
            columns={3}
          />
        )}
        <Toggle label="Humidity" checked={weather.showHumidity} onChange={(showHumidity) => update('weather', { showHumidity })} />
        <Toggle label="UV index" checked={weather.showUv} onChange={(showUv) => update('weather', { showUv })} />
        <Toggle label="Sunrise & sunset" checked={weather.showSun} onChange={(showSun) => update('weather', { showSun })} />
      </Section>
    </WidgetPage>
  );
}

/** Which Microsoft To Do lists appear as tabs in the widget. */
function TodoListChooser() {
  const hidden = useSettings((s) => s.lists.microsoftHidden);
  const update = useSettings((s) => s.update);
  const { data, loading, error } = usePolling('todo-lists-settings', useCallback(() => endpoints.todoLists().then((r) => r.lists), []), 10 * 60_000);
  if (!data) return <p className="muted text-xs">{loading ? 'Loading your To Do lists…' : error}</p>;
  // Several Microsoft accounts: a heading for each account's lists.
  const several = new Set(data.map((l) => l.account)).size > 1;
  return (
    <ul className="space-y-1.5 pl-1">
      {data.map((l, i) => (
        <li key={`${l.account ?? ''}:${l.id}`}>
          {several && l.account !== data[i - 1]?.account && (
            <div className="muted pb-1 pt-2 text-xs font-semibold uppercase tracking-wider">{l.accountEmail ?? 'Microsoft account'}</div>
          )}
          <label className="flex cursor-pointer items-center gap-3 text-sm">
            <input
              type="checkbox"
              checked={!hidden.includes(l.id)}
              onChange={() =>
                update('lists', { microsoftHidden: hidden.includes(l.id) ? hidden.filter((h) => h !== l.id) : [...hidden, l.id] })
              }
              className="accent-[var(--accent)]"
            />
            {l.name}
          </label>
        </li>
      ))}
    </ul>
  );
}

export function ListsTab() {
  const { config, microsoft } = useSession();
  const lists = useSettings((s) => s.lists);
  const update = useSettings((s) => s.update);
  return (
    <WidgetPage label="Show lists" enabled={lists.enabled} onChange={(enabled) => update('lists', { enabled })}>
      <Section title="Lists">
        {config?.features.microsoft && (
          <Choice
            options={[
              { id: 'local', name: 'KCL Canvas lists', description: 'To-do + shopping, synced between screens' },
              { id: 'microsoft', name: 'Microsoft To Do', description: 'Your lists from the linked account' },
            ]}
            value={lists.source}
            onChange={(source) => update('lists', { source })}
          />
        )}
        {lists.source === 'microsoft' && config?.features.microsoft ? (
          microsoft?.connected && microsoft.tasks ? (
            <TodoListChooser />
          ) : (
            <div className="flex items-center justify-between gap-3 text-sm">
              <span className="muted">
                {microsoft?.connected ? 'KCL Canvas needs permission to read your To Do lists.' : 'Connect your Microsoft account first.'}
              </span>
              <button className="shrink-0 text-[var(--accent)] hover:underline" onClick={() => startMicrosoft('link')}>
                {microsoft?.connected ? 'Reconnect Microsoft' : 'Connect Microsoft'}
              </button>
            </div>
          )
        ) : (
          <p className="muted text-xs">Lists sync between screens when you’re signed in.</p>
        )}
      </Section>
    </WidgetPage>
  );
}
